import { getIn, useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Column } from 'components/v2/Column'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { Textarea } from 'components/v2/Textarea'
import { WarningNotification } from 'components/WarningNotification'
import { INewPartnerCostFormValues } from 'features/PartnerCost/types'
import { InputCurrency } from 'components/InputCurrency'
import { Label } from 'components/v2/Label'
import { InputNumber } from 'components/InputNumber'

const messages = defineMessages({
  notePlaceholder: {
    id: 'partnerCost.form.note.placeholder',
    defaultMessage: 'Add a note'
  },
  amountPlaceholder: {
    id: 'partnerCost.form.baseCosts.amount.placeholder',
    defaultMessage: 'Enter the base cost'
  },
  perKm: {
    id: 'partnerCost.form.baseCost.perKm',
    defaultMessage: 'per km'
  }
})

const When = styled.div`
  color: #b0b0b0;
  font-weight: 400;
  align-self: center;
`

interface IBaseCostCardProps {
  isPerKm?: boolean
}

export const BaseCostCard = ({ isPerKm }: IBaseCostCardProps): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, handleBlur, touched, errors } =
    useFormikContext<INewPartnerCostFormValues>()

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="partnerCost.form.section.baseCost.title" defaultMessage="Base Cost" />
      </Heading.H4>
      {isEmpty(values.baseCosts) ? (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="partnerCost.form.baseCosts.empty"
              defaultMessage="You need to select at least one vehicle to add a base cost"
            />
          </EmptyState.Description>
        </EmptyState.Container>
      ) : (
        <>
          {values.baseCosts.map(
            (baseCost, index): ReactElement => (
              <Row key={index}>
                <Column>
                  <Label>
                    <FormattedMessage
                      id="partnerCost.form.baseCost.amount.label"
                      defaultMessage="Base Cost {perKm}"
                      values={{
                        perKm: isPerKm ? intl.formatMessage(messages.perKm) : ''
                      }}
                    />
                  </Label>
                  <InputCurrency
                    id={`baseCosts.${index}.amount`}
                    placeholder={intl.formatMessage(messages.amountPlaceholder)}
                    value={baseCost.amount || ''}
                    onChange={(value: string): void => {
                      setFieldValue(`baseCosts.${index}.amount`, value ? parseFloat(value) : null)
                    }}
                    onBlur={handleBlur}
                    touched={getIn(touched, `baseCosts.${index}.amount`)}
                    errors={getIn(errors, `baseCosts.${index}.amount`)}
                    hideSuffix
                  />
                </Column>
                <When>
                  <FormattedMessage id="dictionary.when" defaultMessage="When" />
                </When>
                <Column>
                  <Label>
                    <FormattedMessage id="partnerCost.form.baseCost.capacity.label" defaultMessage="Capacity" />
                  </Label>
                  <InputNumber
                    id={`baseCosts.${index}.capacity`}
                    value={baseCost.capacity}
                    touched={getIn(touched, `baseCosts.${index}.capacity`)}
                    errors={getIn(errors, `baseCosts.${index}.capacity`)}
                    maskOptions={{ suffix: ' Se\\ats' }}
                    disabled
                  />
                </Column>
              </Row>
            )
          )}
        </>
      )}
      <Row>
        <Column>
          <Textarea
            id="note"
            value={values.note}
            onChange={handleChange}
            onBlur={handleBlur}
            label={<FormattedMessage id="partnerCost.form.note.label" defaultMessage="Add Note" />}
            placeholder={intl.formatMessage(messages.notePlaceholder)}
            touched={getIn(touched, 'note')}
            errors={getIn(errors, 'note')}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <WarningNotification>
            <FormattedMessage
              id="partnerCost.form.baseCosts.warning"
              defaultMessage="This change will be saved and replace the previously assigned cost."
            />
          </WarningNotification>
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
